import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { listingsAPI, wishlistAPI, chatAPI, reportsAPI, reviewsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Heart, MapPin, Eye, Share2, Flag, Calendar, Hash, AlertTriangle, MessageCircle, Tag, Pencil, LogIn, Link2, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import ImageLightbox from '../components/ImageLightbox';
import DealerBadge from '../components/DealerBadge';
import TrustedSellerBadge from '../components/TrustedSellerBadge';

const ListingDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [gone, setGone] = useState(null); // iter123 — {status, title, message} when backend returns 410
  // iter89 — Deep link: /listing/:id?chat=1 auto-triggers the Chat with Seller
  // action once the listing has loaded and the user is authenticated. Used by
  // the homepage card's "Chat with Seller" shortcut and by push-notification
  // deep-links after login.
  const chatDeepLinkTriggered = useRef(false);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [inWishlist, setInWishlist] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [sellerReviews, setSellerReviews] = useState({ reviews: [], average_rating: 0, total: 0 });
  const [newRating, setNewRating] = useState(0);
  const [newReviewText, setNewReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const touchStartX = React.useRef(null);
  const touchEndX = React.useRef(null);

  const goPrevImage = () => {
    if (!listing?.images?.length) return;
    setCurrentImageIndex((i) => (i - 1 + listing.images.length) % listing.images.length);
  };
  const goNextImage = () => {
    if (!listing?.images?.length) return;
    setCurrentImageIndex((i) => (i + 1) % listing.images.length);
  };
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchEndX.current = null; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const delta = touchStartX.current - touchEndX.current;
    if (Math.abs(delta) > 40) (delta > 0 ? goNextImage : goPrevImage)();
    touchStartX.current = null; touchEndX.current = null;
  };
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => { loadListing(); }, [id]);

  const loadListing = async () => {
    try {
      const res = await listingsAPI.getById(id);
      // iter134 — When a hero-banner deep-link opens (`?ad_view=public`), we
      // deliberately IGNORE iter123's owner-bypass. Even the seller viewing
      // their own banner-linked ad should now see "Ad Sold"/"Expired" so
      // desktop matches mobile-anonymous behaviour.
      const isPublicView = searchParams.get('ad_view') === 'public';
      const status = (res.data.status || '').toLowerCase();
      if (isPublicView && status && status !== 'active') {
        setGone({
          status,
          title: res.data.title,
          message: {
            expired: 'This ad has expired and is no longer available.',
            sold:    'This ad has been marked as sold.',
            hidden:  'This ad is currently unavailable.',
            deleted: 'This ad is no longer available.',
          }[status] || 'This ad is no longer available.',
        });
        setLoading(false);
        return;
      }
      setListing(res.data);
      // Load seller reviews
      if (res.data.user_id) {
        reviewsAPI.getSellerReviews(res.data.user_id).then(r => setSellerReviews(r.data)).catch(() => {});
      }
    } catch (e) {
      // iter123 — Backend returns HTTP 410 Gone with `detail` = {status, title, message}
      // for any non-active listing (expired / sold / hidden / deleted) that a
      // non-owner tries to open via direct URL, hero banner, shared link, bookmark
      // or SEO crawl. Render the appropriate expired-listing screen instead of
      // rendering the ad as though it were live.
      if (e?.response?.status === 410) {
        const info = e.response.data?.detail || {};
        setGone({
          status: info.status || 'expired',
          title: info.title || null,
          message: info.message || 'This ad is no longer available.',
        });
      } else {
        console.error(e);
        toast.error('Failed to load listing');
      }
    } finally { setLoading(false); }
  };

  const handleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (inWishlist) {
        await wishlistAPI.remove(id);
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistAPI.add(id);
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (e) { toast.error('Failed to update wishlist'); }
  };

  const handleMakeOffer = async () => {
    if (!user) { navigate('/login'); return; }
    if (!offerAmount || parseFloat(offerAmount) <= 0) { toast.error('Enter a valid offer amount'); return; }
    try {
      const res = await chatAPI.startConversation({
        listing_id: id,
        seller_id: listing.user_id,
        offer_amount: parseFloat(offerAmount),
        message: offerMessage
      });
      toast.success('Offer sent! Check your messages.');
      navigate(`/chat/${res.data.conversation_id}`);
    } catch (e) { toast.error('Failed to send offer'); }
  };

  const handleContactSeller = async () => {
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/listing/${id}?chat=1`)}`); return; }
    try {
      const res = await chatAPI.startConversation({
        listing_id: id,
        seller_id: listing.user_id,
        offer_amount: 0,
        message: `Hi, I'm interested in "${listing.title}"`
      });
      navigate(`/chat/${res.data.conversation_id}`);
    } catch (e) {
      // iter128 — On iOS WKWebView we occasionally hit a 401 here because
      // the stored session token has been rotated by another tab / cold
      // start; the api.js interceptor already tries /auth/refresh once,
      // so a 401 that reaches us means the refresh failed too. Send the
      // user to /login (preserving the deep-link back to this listing +
      // chat intent) instead of just showing a generic red toast that
      // stranded iPhone users with no next step.
      if (e?.response?.status === 401) {
        navigate(`/login?next=${encodeURIComponent(`/listing/${id}?chat=1`)}`);
        return;
      }
      toast.error(e?.response?.data?.detail || 'Failed to start chat');
    }
  };

  // iter89 — Deep-link auto-trigger. Fires once when the listing is loaded
  // AND ?chat=1 is present AND the viewer is signed in AND isn't the seller.
  // The `chatDeepLinkTriggered` ref makes this idempotent across re-renders.
  useEffect(() => {
    if (chatDeepLinkTriggered.current) return;
    if (!listing || !user) return;
    if (searchParams.get('chat') !== '1') return;
    if (user.user_id === listing.user_id) return;
    chatDeepLinkTriggered.current = true;
    handleContactSeller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing, user, searchParams]);

  const handleReport = async () => {
    if (!user) { navigate('/login'); return; }
    if (!reportReason) { toast.error('Please select a reason'); return; }
    try {
      await reportsAPI.create({ listing_id: id, reported_user_id: listing.user_id, reason: reportReason });
      toast.success('Report submitted');
      setShowReport(false);
    } catch (e) { toast.error('Failed to submit report'); }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out ${listing.title} on GenzoX - ${'\u20B9'}${listing.price.toLocaleString()}`;
    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    else if (platform === 'copy') {
      try {
        navigator.clipboard.writeText(url).then(() => {
          toast.success('Link copied!');
        }).catch(() => {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          toast.success('Link copied!');
        });
      } catch (e) {
        toast.error('Could not copy link');
      }
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (<div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-[#1800ad]"></div>
    </div>);
  }
  if (gone) {
    // iter123/iter135 — Expired / sold / hidden ad landing page. Fired for
    // any non-owner opening a non-active listing (direct URL, hero banner
    // deep link, shared link, bookmark, SEO crawler, etc.). iter135 —
    // Rewrapped in a normal-page container (not full-viewport centered)
    // so the globally-mounted SiteFooter renders below the card and the
    // page-level Back link at the top gives users a nav escape.
    const label = gone.status === 'sold'
      ? 'Ad Sold'
      : gone.status === 'hidden'
        ? 'Ad Unavailable'
        : 'Ad Expired';
    return (
      <div className="min-h-screen bg-[#F8F9FA] py-4 sm:py-6" data-testid="listing-gone-screen">
        <div className="max-w-7xl mx-auto px-3 sm:px-8">
          <Link to="/" className="text-[#6B7280] hover:text-[#1800ad] font-medium mb-3 sm:mb-5 inline-block text-xs sm:text-sm" data-testid="listing-gone-back-link">&larr; Back to Home</Link>
          <div className="w-full max-w-md mx-auto text-center bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mt-6 sm:mt-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#fff4ed] text-[#ff8500] mb-4">
              <AlertTriangle size={26} />
            </div>
            <p data-testid="listing-gone-label" className="text-lg sm:text-xl font-bold text-[#0A0B10] mb-1.5">{label}</p>
            {gone.title && (
              <p className="text-sm text-[#6B7280] mb-2 line-clamp-2" data-testid="listing-gone-title">{gone.title}</p>
            )}
            <p className="text-sm text-[#6B7280] mb-5" data-testid="listing-gone-message">{gone.message}</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <Link to="/" data-testid="listing-gone-home" className="inline-flex items-center justify-center bg-[#1800ad] text-white rounded-full px-6 py-2.5 text-sm font-bold hover:bg-[#12008a] transition-colors">
                Browse Latest Ads
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!listing) {
    return (<div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="text-center"><p className="text-xl font-bold text-[#0A0B10] mb-3">Listing not found</p>
        <Link to="/" className="text-[#1800ad] hover:underline font-semibold">Back to Home</Link></div>
    </div>);
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 sm:py-6">
      <div className="max-w-7xl mx-auto px-3 sm:px-8">
        <Link to="/" className="text-[#6B7280] hover:text-[#1800ad] font-medium mb-3 sm:mb-5 inline-block text-xs sm:text-sm" data-testid="back-link">&larr; Back to Home</Link>

        {/* iter135 — When the OWNER views their own non-active listing via
             iter123's owner-bypass path (200 OK on sold/expired/hidden ads
             without ?ad_view=public), render a prominent status banner up
             top and suppress buyer CTAs (Chat, Make Offer, Save, Report)
             below so the owner isn't misled about the ad's public state. */}
        {(() => {
          const ownerStatus = (listing.status || '').toLowerCase();
          const isOwner = user && user.user_id === listing.user_id;
          if (!isOwner || !ownerStatus || ownerStatus === 'active') return null;
          const bannerLabel = ownerStatus === 'sold' ? 'Marked as Sold'
            : ownerStatus === 'expired' ? 'This ad has Expired'
            : ownerStatus === 'hidden' ? 'This ad is Hidden'
            : `Status: ${ownerStatus}`;
          const bannerMsg = ownerStatus === 'sold'
            ? 'Buyers cannot see this ad any more. You still have access as the owner.'
            : ownerStatus === 'expired'
              ? 'Buyers cannot see this ad. Republish it from your dashboard to bring it back.'
              : 'Buyers cannot see this ad. Edit it from your dashboard to change its state.';
          return (
            <div className="mb-4 sm:mb-6 rounded-2xl border border-[#ff8500]/30 bg-[#fff4ed] p-4 sm:p-5 flex items-start gap-3" data-testid="owner-status-banner">
              <div className="w-10 h-10 rounded-full bg-[#ff8500]/20 text-[#ff8500] flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <p className="text-sm sm:text-base font-bold text-[#0A0B10]" data-testid="owner-status-banner-label">{bannerLabel}</p>
                <p className="text-xs sm:text-sm text-[#6B7280] mt-0.5">{bannerMsg}</p>
              </div>
              <Link to={`/edit-listing/${listing.listing_id}`} data-testid="owner-status-banner-edit" className="hidden sm:inline-flex items-center gap-1.5 bg-white text-[#ff8500] border border-[#ff8500]/30 rounded-full px-4 py-1.5 text-xs font-bold hover:bg-[#ff8500] hover:text-white transition-colors">
                <Pencil size={12} /> Edit Ad
              </Link>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Image Gallery - 3 cols */}
          <div className="lg:col-span-3 lg:col-start-1 lg:row-start-1" data-testid="image-gallery">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              {listing.images && listing.images.length > 0 ? (
                <>
                  <div
                    className="relative cursor-pointer select-none"
                    onClick={() => setLightboxOpen(true)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    data-testid="main-image-clickable"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${listing.images[currentImageIndex]}`}
                      alt={listing.title}
                      className="w-full h-80 sm:h-96 object-contain bg-gray-50 rounded-xl mb-3"
                    />
                    {listing.images_purge_state === 'purged' && (
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg px-3 py-1.5 shadow text-xs font-bold text-[#6B7280]" data-testid="images-archived-badge">
                        Images archived
                      </div>
                    )}
                    {listing.images.length > 1 && (
                      <>
                        <button
                          type="button"
                          data-testid="slider-prev-btn"
                          onClick={(e) => { e.stopPropagation(); goPrevImage(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 flex items-center justify-center text-[#1800ad] transition-colors"
                          aria-label="Previous image"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          type="button"
                          data-testid="slider-next-btn"
                          onClick={(e) => { e.stopPropagation(); goNextImage(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 flex items-center justify-center text-[#1800ad] transition-colors"
                          aria-label="Next image"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/55 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full" data-testid="slider-counter">
                          {currentImageIndex + 1} / {listing.images.length}
                        </div>
                      </>
                    )}
                  </div>
                  {listing.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                      {listing.images.map((img, i) => (
                        <img key={img} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt=""
                          onClick={() => setCurrentImageIndex(i)}
                          className={`w-16 h-16 object-contain bg-gray-50 rounded-lg cursor-pointer border-2 transition-all ${i === currentImageIndex ? 'border-[#1800ad]' : 'border-transparent opacity-60 hover:opacity-100'}`} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-80 bg-gray-100 rounded-xl flex items-center justify-center">
                  <span className="text-gray-400">{t('no_image')}</span></div>
              )}
            </div>
          </div>

          {/* Lightbox */}
          {lightboxOpen && listing.images && listing.images.length > 0 && (
            <ImageLightbox
              images={listing.images}
              initialIndex={currentImageIndex}
              onClose={() => setLightboxOpen(false)}
              baseUrl={process.env.REACT_APP_BACKEND_URL}
            />
          )}

          {/* Info - 2 cols. iter127 — on desktop this column spans 2 rows so
               that the below-gallery extras (Disclaimer / Safety Tips / Seller
               Reviews) can sit directly beneath the gallery in the left column
               without leaving unused vertical whitespace. */}
          <div className="lg:col-span-2 lg:col-start-4 lg:row-start-1 lg:row-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-4" data-testid="listing-info">
              <div className="flex flex-wrap gap-2 mb-3">
                {listing.is_dealer_listing && <DealerBadge testId="listing-detail-dealer-badge" />}
                {listing.is_trusted_seller && !listing.is_dealer_listing && (
                  <TrustedSellerBadge size="md" testId="listing-detail-trusted-badge" />
                )}
                {listing.is_pro && <span className="bg-gradient-to-r from-[#1800ad] to-[#ff8500] text-white text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase">PRO</span>}
                {listing.is_boosted && <span className="bg-[#fff4ed] text-[#ff8500] text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase border border-[#ff8500]/20">BOOSTED</span>}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#0A0B10] mb-2 sm:mb-3">{listing.title}</h1>
              <p className="text-2xl sm:text-3xl font-bold text-[#1800ad] mb-3 sm:mb-4">{'\u20B9'}{listing.price.toLocaleString()}</p>

              {/* Edit Ad Button */}
              {user && user.user_id === listing.user_id ? (
                <Link to={`/edit-listing/${listing.listing_id}`} data-testid="edit-ad-btn"
                  className="w-full mb-3 bg-[#fff4ed] text-[#ff8500] border border-[#ff8500]/20 rounded-xl py-2.5 font-bold text-sm hover:bg-[#ff8500] hover:text-white transition-colors flex items-center justify-center gap-2">
                  <Pencil size={16} /> Edit This Ad
                </Link>
              ) : !user ? (
                <Link to="/login" data-testid="login-to-edit-btn"
                  className="w-full mb-3 bg-gray-50 text-[#6B7280] border border-gray-200 rounded-xl py-2.5 font-bold text-xs sm:text-sm hover:bg-[#f0edff] hover:text-[#1800ad] hover:border-[#1800ad]/20 transition-colors flex items-center justify-center gap-2">
                  <LogIn size={14} /> Login to Edit
                </Link>
              ) : null}

              {/* Meta info */}
              <div className="flex flex-wrap gap-3 text-xs text-[#6B7280] mb-4">
                <span className="flex items-center gap-1"><MapPin size={13} />{listing.location}</span>
                <span className="flex items-center gap-1"><Eye size={13} />{listing.views} {t('views')}</span>
                <span className="flex items-center gap-1"><Calendar size={13} />{t('posted')} {formatDate(listing.created_at)}</span>
                <span className="flex items-center gap-1" data-testid="ad-id"><Hash size={13} />{t('ad_id')}: {listing.listing_id.slice(-8).toUpperCase()}</span>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <h3 className="text-sm font-bold text-[#0A0B10] mb-2">Description</h3>
                {/* iter127 — description is authored as sanitised HTML via
                     TipTap; render with DOMPurify (allow-list only inline
                     formatting marks + textStyle spans) so buyers see the
                     same layout the seller composed. */}
                <div
                  data-testid="listing-description"
                  className="text-sm text-[#6B7280] leading-relaxed [&_p]:my-1 [&_strong]:text-[#0A0B10] [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(listing.description || '', {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span'],
                      ALLOWED_ATTR: ['style'],
                    }),
                  }}
                />
              </div>

              {/* Seller info. iter127 — click routes to /seller/:userId to
                   show all active listings by this seller. */}
              {listing.seller && (
                <Link
                  to={`/seller/${listing.seller.user_id || listing.user_id}`}
                  data-testid="seller-info"
                  className={`flex items-center gap-3 p-3 rounded-xl mb-4 transition-shadow hover:shadow-md ${listing.seller.is_dealer ? 'bg-[#00bf63]/10 border border-[#00bf63]/30' : 'bg-[#f0edff]'}`}
                >
                  {listing.seller.picture ? (
                    <img src={listing.seller.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${listing.seller.is_dealer ? 'bg-[#00bf63]' : 'bg-[#1800ad]'}`}>
                      {listing.seller.name?.[0] || 'U'}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-bold text-sm text-[#0A0B10]">
                      {listing.seller.business_name && listing.seller.is_dealer
                        ? listing.seller.business_name
                        : listing.seller.name}
                    </p>
                    {listing.seller.business_name && listing.seller.is_dealer && (
                      <p className="text-[10px] text-[#6B7280]">via {listing.seller.name}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {listing.seller.is_dealer && (
                        <DealerBadge variant="detail" testId="seller-info-dealer-badge" />
                      )}
                      {listing.seller.aadhaar_verified && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Verified</span>}
                      {listing.seller.email_verified && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Email</span>}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-[#6B7280] flex-shrink-0" />
                </Link>
              )}

              {/* Action Buttons */}
              {/* iter135 — Suppress buyer CTAs (Save, Chat, Make Offer,
                   Report) when the OWNER is viewing their own non-active
                   listing via iter123's owner-bypass. Owner already sees
                   the status banner up top + the Edit This Ad button. */}
              {(() => {
                const ownerStatus = (listing.status || '').toLowerCase();
                const isOwner = user && user.user_id === listing.user_id;
                const suppressBuyerCtas = isOwner && ownerStatus && ownerStatus !== 'active';
                if (suppressBuyerCtas) return null;
                return (
                  <>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
                      <button data-testid="wishlist-btn" onClick={handleWishlist}
                        className={`rounded-xl py-2.5 sm:py-3 font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 ${inWishlist ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-red-50/60 text-red-600 border border-red-200 hover:bg-red-100'}`}>
                        <Heart size={15} className="text-red-500" fill={inWishlist ? '#ef4444' : 'none'} /> {inWishlist ? t('saved') : t('save')}
                      </button>
                      <button data-testid="contact-seller-btn" onClick={handleContactSeller}
                        className="bg-[#1800ad] text-white rounded-xl py-2.5 sm:py-3 font-bold text-xs sm:text-sm hover:bg-[#1200a0] transition-colors flex items-center justify-center gap-1.5 shadow-lg shadow-[#1800ad]/20">
                        <MessageCircle size={15} /> Chat with Seller
                      </button>
                    </div>

                    <button data-testid="make-offer-btn" onClick={() => setShowOffer(!showOffer)}
                      className="w-full bg-[#ff8500] text-white rounded-xl py-2.5 sm:py-3 font-bold text-xs sm:text-sm hover:bg-[#e57500] transition-colors flex items-center justify-center gap-1.5 shadow-lg shadow-[#ff8500]/20 mb-3">
                      <Tag size={15} /> {t('make_offer')}
                    </button>

                    {/* Make Offer Form */}
                    {showOffer && (
                      <div className="bg-[#fff9f5] border border-[#ff8500]/20 rounded-xl p-4 mb-3" data-testid="offer-form">
                        <input data-testid="offer-amount-input" type="number" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)}
                          placeholder={`${t('offer_price')} (${'\u20B9'})`}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm mb-2 focus:border-[#ff8500] focus:ring-2 focus:ring-[#ff8500]/10 outline-none" />
                        <input type="text" value={offerMessage} onChange={(e) => setOfferMessage(e.target.value)}
                          placeholder="Add a message (optional)"
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm mb-2 focus:border-[#ff8500] focus:ring-2 focus:ring-[#ff8500]/10 outline-none" />
                        <button data-testid="submit-offer-btn" onClick={handleMakeOffer}
                          className="w-full bg-[#ff8500] text-white rounded-lg py-2.5 font-bold text-sm hover:bg-[#e57500] transition-colors">
                          {t('submit_offer')}
                        </button>
                      </div>
                    )}

                    {/* Share & Report */}
                    <div className="flex gap-2">
                      <button data-testid="share-whatsapp-btn" onClick={() => handleShare('whatsapp')}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors text-xs font-bold">
                        <Share2 size={13} /> WhatsApp
                      </button>
                      <button data-testid="share-copy-btn" onClick={() => handleShare('copy')}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-100 text-[#6B7280] hover:bg-gray-200 transition-colors text-xs font-bold">
                        <Link2 size={13} /> Copy Link
                      </button>
                      <button data-testid="report-ad-btn" onClick={() => setShowReport(!showReport)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-xs font-bold">
                        <Flag size={13} /> {t('report_ad')}
                      </button>
                    </div>
                  </>
                );
              })()}

              {/* Report Form */}
              {showReport && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="report-form">
                  <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm mb-2 outline-none">
                    <option value="">Select reason...</option>
                    <option value="spam">Spam / Irrelevant</option>
                    <option value="fake">Fake Listing</option>
                    <option value="offensive">Offensive Content</option>
                    <option value="scam">Possible Scam</option>
                    <option value="other">Other</option>
                  </select>
                  <button data-testid="submit-report-btn" onClick={handleReport}
                    className="w-full bg-red-600 text-white rounded-lg py-2.5 font-bold text-sm hover:bg-red-700 transition-colors">
                    Submit Report
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* iter127 — Extras (Disclaimer / Safety Tips / Seller Reviews)
               placed beneath the gallery on desktop (left column, row 2) so
               important trust content lands above-the-fold instead of below
               the info card. On mobile they naturally stack after the info
               card because the outer grid collapses to a single column. */}
          <div className="lg:col-span-3 lg:col-start-1 lg:row-start-2 space-y-4" data-testid="listing-extras">
            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="disclaimer">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">{t('disclaimer')}</p>
              </div>
            </div>

            {/* Safety Tips */}
            <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="safety-tips">
              <h3 className="text-sm font-bold text-[#0A0B10] mb-3">{t('safety_tips')}</h3>
              <ul className="space-y-2 text-xs text-[#6B7280]">
                <li className="flex items-start gap-2"><span className="text-green-500 font-bold">&#10003;</span>{t('meet_safe')}</li>
                <li className="flex items-start gap-2"><span className="text-green-500 font-bold">&#10003;</span>{t('check_item')}</li>
                <li className="flex items-start gap-2"><span className="text-green-500 font-bold">&#10003;</span>{t('pay_after')}</li>
                <li className="flex items-start gap-2"><span className="text-red-500 font-bold">!</span>{t('never_share')}</li>
              </ul>
            </div>

            {/* Seller Reviews */}
            <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="seller-reviews">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#0A0B10]">Seller Reviews</h3>
                {sellerReviews.total > 0 && (
                  <div className="flex items-center gap-1">
                    <Star size={14} className="text-[#F59E0B]" fill="#F59E0B" />
                    <span className="text-sm font-bold text-[#0A0B10]">{sellerReviews.average_rating}</span>
                    <span className="text-xs text-[#9CA3AF]">({sellerReviews.total})</span>
                  </div>
                )}
              </div>

              {/* Write review */}
              {user && user.user_id !== listing?.user_id && (
                <div className="mb-4 p-3 bg-gray-50 rounded-xl" data-testid="write-review">
                  <div className="flex gap-1 mb-2">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setNewRating(s)} data-testid={`star-${s}`}
                        className="transition-colors">
                        <Star size={18} className={s <= newRating ? 'text-[#F59E0B]' : 'text-gray-300'} fill={s <= newRating ? '#F59E0B' : 'none'} />
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newReviewText} onChange={e => setNewReviewText(e.target.value)} placeholder="Write a review..."
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs" data-testid="review-input" />
                    <button disabled={!newRating || submittingReview} data-testid="submit-review-btn"
                      onClick={async () => {
                        if (!newRating) return;
                        setSubmittingReview(true);
                        try {
                          await reviewsAPI.create({ seller_id: listing.user_id, listing_id: listing.listing_id, rating: newRating, comment: newReviewText });
                          toast.success('Review submitted!');
                          setNewRating(0); setNewReviewText('');
                          const r = await reviewsAPI.getSellerReviews(listing.user_id);
                          setSellerReviews(r.data);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit review'); }
                        finally { setSubmittingReview(false); }
                      }}
                      className="bg-[#1800ad] text-white rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-50">
                      Submit
                    </button>
                  </div>
                </div>
              )}

              {sellerReviews.reviews.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] text-center py-2">No reviews yet</p>
              ) : sellerReviews.reviews.slice(0, 5).map((r, i) => (
                <div key={i} className="py-2 border-t border-gray-50 first:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-[#0A0B10]">{r.reviewer?.name || 'User'}</span>
                    <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} size={10} className={s <= r.rating ? 'text-[#F59E0B]' : 'text-gray-200'} fill={s <= r.rating ? '#F59E0B' : 'none'} />)}</div>
                  </div>
                  {r.comment && <p className="text-xs text-[#6B7280]">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
